import { useEffect, useMemo, useRef, useState } from 'react';

import { OrgAvatar } from '../Avatar';
import Button from '../Input2/Button';
import FilterSelect from '../Input2/FilterSelect';
import Select from '../Input2/Select';
import SingleCheckbox from '../Input2/SingleCheckbox';
import Popup from '../Popup/Popup';
import { General } from '../StrokeIcons';

export type OrgSearchOption = {
  id: string;
  name: string;
  pictureUrl: string | null;
};

export type AddExperienceInput = {
  orgId: string;
  startMonth?: number;
  startYear?: number;
  endMonth?: number;
  endYear?: number;
  current?: boolean;
};

export type AddExperiencePopupProps = {
  onClose: () => void;
  onSubmit: (input: AddExperienceInput) => Promise<void>;
  orgSearch: (query: string) => Promise<OrgSearchOption[]>;
  contactFirstName?: string | null;
};

const MONTHS = [
  { value: '1', label: 'Jan' },
  { value: '2', label: 'Feb' },
  { value: '3', label: 'Mar' },
  { value: '4', label: 'Apr' },
  { value: '5', label: 'May' },
  { value: '6', label: 'Jun' },
  { value: '7', label: 'Jul' },
  { value: '8', label: 'Aug' },
  { value: '9', label: 'Sep' },
  { value: '10', label: 'Oct' },
  { value: '11', label: 'Nov' },
  { value: '12', label: 'Dec' },
];

const YEAR_SPAN = 50;

function buildYearOptions() {
  const current = new Date().getFullYear();
  const years: { value: string; label: string }[] = [];
  for (let y = current; y >= current - YEAR_SPAN; y--) {
    years.push({ value: String(y), label: String(y) });
  }
  return years;
}

function parseOptional(value: string): number | undefined {
  if (value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export default function AddExperiencePopup({
  onClose,
  onSubmit,
  orgSearch,
  contactFirstName,
}: AddExperiencePopupProps) {
  const trimmedFirstName = contactFirstName?.trim();
  const currentlyWorksHereLabel =
    trimmedFirstName != null && trimmedFirstName.length > 0
      ? `${trimmedFirstName} currently works here`
      : 'Currently works here';
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [orgOptions, setOrgOptions] = useState<OrgSearchOption[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [startMonth, setStartMonth] = useState<string>('');
  const [startYear, setStartYear] = useState<string>('');
  const [endMonth, setEndMonth] = useState<string>('');
  const [endYear, setEndYear] = useState<string>('');
  const [current, setCurrent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const yearOptions = useMemo(() => buildYearOptions(), []);

  // Track the most recent search so out-of-order resolutions don't clobber the list.
  const searchSeq = useRef(0);

  async function runSearch(query: string) {
    const seq = ++searchSeq.current;
    setIsLoadingOrgs(true);
    try {
      const results = await orgSearch(query);
      if (seq !== searchSeq.current) return;
      setOrgOptions(results);
    } finally {
      if (seq === searchSeq.current) setIsLoadingOrgs(false);
    }
  }

  useEffect(() => {
    void runSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedOrg = orgOptions.find((o) => o.id === orgId);

  const filterOptions = orgOptions.map((o) => ({
    value: o.id,
    label: o.name,
    icon: <OrgAvatar src={o.pictureUrl} name={o.name} className="size-4" shape="square" />,
  }));

  const canSubmit = orgId != null && !isSubmitting;

  async function handleSave() {
    if (orgId == null) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        orgId,
        startMonth: parseOptional(startMonth),
        startYear: parseOptional(startYear),
        endMonth: current ? undefined : parseOptional(endMonth),
        endYear: current ? undefined : parseOptional(endYear),
        current: current ? true : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Popup onClose={onClose} className="w-[440px]! max-w-[440px]! text-left!">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add experience</h2>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Organization</label>
        <FilterSelect
          options={filterOptions}
          value={orgId}
          onChange={(v) => setOrgId(v)}
          onFilterInputChange={(q) => void runSearch(q)}
          isLoadingOptions={isLoadingOrgs}
          placeholder="Select an organization"
          icon={
            selectedOrg ? (
              <OrgAvatar src={selectedOrg.pictureUrl} name={selectedOrg.name} className="size-4" shape="square" />
            ) : (
              <General.Building07 />
            )
          }
          filterable
          autoWidth
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-600 dark:text-gray-300">Start date</label>
        <div className="flex gap-2">
          <Select
            options={[{ value: '', label: 'Month' }, ...MONTHS]}
            value={startMonth}
            onChange={(e) => setStartMonth(e.target.value)}
            placeholder="Month"
            className="flex-1"
          />
          <Select
            options={[{ value: '', label: 'Year' }, ...yearOptions]}
            value={startYear}
            onChange={(e) => setStartYear(e.target.value)}
            placeholder="Year"
            className="flex-1"
          />
        </div>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
        <SingleCheckbox checked={current} onChange={setCurrent} size="sm" />
        {currentlyWorksHereLabel}
      </label>

      {!current && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-300">End date</label>
          <div className="flex gap-2">
            <Select
              options={[{ value: '', label: 'Month' }, ...MONTHS]}
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              placeholder="Month"
              className="flex-1"
            />
            <Select
              options={[{ value: '', label: 'Year' }, ...yearOptions]}
              value={endYear}
              onChange={(e) => setEndYear(e.target.value)}
              placeholder="Year"
              className="flex-1"
            />
          </div>
        </div>
      )}

      <div className="mt-2 flex justify-end gap-2">
        <Button color="gray" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={handleSave}
          disabled={!canSubmit}
          status={isSubmitting ? 'updating' : undefined}>
          Save
        </Button>
      </div>
    </Popup>
  );
}
